import request from '../../../../core/wrappers/axios-wrapper';
import { ApiURL } from '../../../../types/constants/apiUrls/api-urls';
import { RequestType } from '../../../../types/enums/request-enums';

export type BatchOutputItem = {
    rawImageId: number;
    barcode: string;
    fileName: string;
    filePath: string;
    processedFilePath: string;
    llmProcessedFilePath: string;
    photoTypeCode: string;
    photoTypeDisplayName: string;
    status: string;
    rejectKey: string;
    rejectNote: string;
    specialCode: string;
    colorCode: string;
    productName: string;
    createDate: string;
};

export type BatchOutputListResponse = {
    items: BatchOutputItem[];
    totalCount: number;
    pageNumber: number;
    pageSize: number;
    pendingCount: number;
    approvedCount: number;
    rejectedCount: number;
};

export type BatchOutputRejectReason = {
    key: string;
    labelTr: string;
    labelEn: string;
};

export type BatchOutputListParams = {
    pageNumber?: number;
    pageSize?: number;
    queue?: 'Pending' | 'Rejected' | string;
    barcode?: string;
    /** Optional; BackofficeBFF forces current user for non-supervisors. */
    assignee?: string;
};

const pick = (raw: Record<string, unknown>, camel: string, pascal: string) =>
    raw[camel] ?? raw[pascal];

const mapItem = (item: Record<string, unknown>): BatchOutputItem => ({
    rawImageId: Number(pick(item, 'rawImageId', 'RawImageId') ?? 0),
    barcode: String(pick(item, 'barcode', 'Barcode') ?? ''),
    fileName: String(pick(item, 'fileName', 'FileName') ?? ''),
    filePath: String(pick(item, 'filePath', 'FilePath') ?? ''),
    processedFilePath: String(pick(item, 'processedFilePath', 'ProcessedFilePath') ?? ''),
    llmProcessedFilePath: String(pick(item, 'llmProcessedFilePath', 'LlmProcessedFilePath') ?? ''),
    photoTypeCode: String(pick(item, 'photoTypeCode', 'PhotoTypeCode') ?? ''),
    photoTypeDisplayName: String(pick(item, 'photoTypeDisplayName', 'PhotoTypeDisplayName') ?? ''),
    status: String(pick(item, 'status', 'Status') ?? ''),
    rejectKey: String(pick(item, 'rejectKey', 'RejectKey') ?? ''),
    rejectNote: String(pick(item, 'rejectNote', 'RejectNote') ?? ''),
    specialCode: String(pick(item, 'specialCode', 'SpecialCode') ?? ''),
    colorCode: String(pick(item, 'colorCode', 'ColorCode') ?? ''),
    productName: String(pick(item, 'productName', 'ProductName') ?? ''),
    createDate: String(pick(item, 'createDate', 'CreateDate') ?? ''),
});

const GetItems = async (params: BatchOutputListParams): Promise<BatchOutputListResponse> => {
    const raw = (await request({
        url: ApiURL.Query.StudioQueryURLs.BatchOutputQueryURL.GetItems,
        method: RequestType.GET,
        params,
    })) as Record<string, unknown>;

    const items = ((pick(raw, 'items', 'Items') ?? []) as Record<string, unknown>[]).map(mapItem);

    return {
        items,
        totalCount: Number(pick(raw, 'totalCount', 'TotalCount') ?? 0),
        pageNumber: Number(pick(raw, 'pageNumber', 'PageNumber') ?? 1),
        pageSize: Number(pick(raw, 'pageSize', 'PageSize') ?? 20),
        pendingCount: Number(pick(raw, 'pendingCount', 'PendingCount') ?? 0),
        approvedCount: Number(pick(raw, 'approvedCount', 'ApprovedCount') ?? 0),
        rejectedCount: Number(pick(raw, 'rejectedCount', 'RejectedCount') ?? 0),
    };
};

const GetRejectReasons = async (): Promise<BatchOutputRejectReason[]> => {
    const raw = (await request({
        url: ApiURL.Query.StudioQueryURLs.BatchOutputQueryURL.GetRejectReasons,
        method: RequestType.GET,
    })) as unknown;

    const list = Array.isArray(raw) ? raw : [];
    return list.map((entry) => {
        const item = entry as Record<string, unknown>;
        return {
            key: String(pick(item, 'key', 'Key') ?? ''),
            labelTr: String(pick(item, 'labelTr', 'LabelTr') ?? ''),
            labelEn: String(pick(item, 'labelEn', 'LabelEn') ?? ''),
        };
    });
};

export const BatchOutputQuery = {
    GetItems,
    GetRejectReasons,
};
